// Publish / unpublish a digital edition.
//
// Members gate on `status='active'`, so this one flip controls the issue
// list, the search index and the reader at once. Mirrors usePublishArticle
// so the two read the same way.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'

export const ISSUE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  active: 'Published',
  deleted: 'Retired',
}

export const ISSUE_STATUS_CLASS: Record<string, string> = {
  draft: 'border-amber-glow/60 text-amber-glow',
  active: 'border-radar-green/50 text-radar-green',
  deleted: 'border-paper-trace text-paper-trace',
}

export function usePublishIssue(editionId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (published: boolean) =>
      api.patch(`/api/lab/issues/${editionId}/status`, { published }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab', 'issues'] })
      qc.invalidateQueries({ queryKey: ['lab', 'issues', 'detail'] })
    },
  })
}

/** The API's structured detail message, or a readable fallback.
 *
 *  ApiError.detail is the whole response body, and FastAPI serialises
 *  HTTPException(detail={...}) as {"detail": {...}} — so the message an
 *  operator should read sits one level deeper than `detail` itself. */
export function issueActionError(error: unknown): string | null {
  if (!error) return null
  const body = (error as { detail?: unknown }).detail
  if (typeof body === 'string') return body
  const inner = (body as { detail?: unknown } | undefined)?.detail
  if (typeof inner === 'string') return inner
  const message = (inner as { message?: string } | undefined)?.message
  if (message) return message
  if (error instanceof Error && error.message) return error.message
  return 'The request failed.'
}
